/**
 * Browser session key ("delegate").
 *
 * A throwaway keypair generated in the browser and kept in localStorage. The
 * player authorises it once, on-chain, with a bounded allowance and deadline;
 * after that every card is signed locally with no wallet popup and no gas.
 *
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │ WHAT THIS KEY CAN AND CANNOT DO.                                      │
 * │                                                                       │
 * │ It can spend, as stake, up to the allowance the player granted, until │
 * │ the deadline passes. That is all. It cannot withdraw, cannot move     │
 * │ funds to another address, cannot touch a different player's balance,  │
 * │ and cannot raise its own allowance — every one of those is enforced   │
 * │ on-chain, not here.                                                   │
 * │                                                                       │
 * │ So the realistic worst case for a stolen session key is: someone      │
 * │ burns the remaining allowance playing cards, and any winnings land    │
 * │ back in the player's own withdrawable balance. Sized accordingly      │
 * │ (default: one session's spend, not the whole balance), that is a      │
 * │ bounded loss rather than a drained account.                           │
 * │                                                                       │
 * │ localStorage is readable by any script on this origin, which is why   │
 * │ the CSP in public/_headers matters: script-src 'self' with no CDN     │
 * │ origins is what keeps third-party code off the page.                  │
 * └───────────────────────────────────────────────────────────────────────┘
 */
import { Wallet, hexlify, randomBytes } from "ethers";

const KEY_PREFIX = "scratchy:sessionKey:";

/** Namespaced per wallet so switching accounts never reuses a key. */
function storageKey(account: string): string {
  return KEY_PREFIX + account.toLowerCase();
}

/**
 * Return the session key for `account`, creating one on first use.
 *
 * Never returns a key belonging to a different account: switching wallets
 * yields a different storage slot and therefore a different delegate, which
 * must then be authorised separately.
 */
export function sessionKeyFor(account: string): Wallet {
  const k = storageKey(account);
  let pk: string | null = null;
  try {
    pk = localStorage.getItem(k);
  } catch {
    /* private mode / storage disabled — fall through to an ephemeral key */
  }

  if (pk && /^0x[0-9a-fA-F]{64}$/.test(pk)) {
    return new Wallet(pk);
  }

  const fresh = hexlify(randomBytes(32));
  try {
    localStorage.setItem(k, fresh);
  } catch {
    /* Ephemeral: the key lives for this page load only. The player will be
       asked to re-authorise on reload, which is correct — better than silently
       failing to persist and looking broken. */
  }
  return new Wallet(fresh);
}

/** Forget the session key. The on-chain allowance must be revoked separately. */
export function clearSessionKey(account: string): void {
  try {
    localStorage.removeItem(storageKey(account));
  } catch {
    /* nothing to do */
  }
}

/** A fresh 32-byte player randomness share, which doubles as the permit nonce. */
export function newPlayerSeed(): string {
  return hexlify(randomBytes(32));
}
